import React, { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { useLanguage } from "@/contexts/LanguageContext";

const ABOUT_IMG =
  "https://customer-assets.emergentagent.com/job_lopez-premium/artifacts/dngwk11x_image.png";

function Counter({ to, suffix = "", labelKey, duration = 1600 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  const [n, setN] = useState(0);
  const { t } = useLanguage();

  useEffect(() => {
    if (!inView) return;
    let raf;
    const start = performance.now();
    const step = (now) => {
      const p = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setN(Math.floor(eased * to));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [inView, to, duration]);

  // For non-numeric counters (rating "5"), still animates from 0 → to
  return (
    <div ref={ref} className="text-center" data-testid={`counter-${labelKey}`}>
      <div className="font-serif-display text-4xl sm:text-5xl md:text-6xl gold-gradient-text leading-tight whitespace-nowrap">
        {n}
        {suffix}
      </div>
      <div className="mt-3 text-[10px] sm:text-[11px] tracking-[0.3em] uppercase text-white/65">
        {t(`about.${labelKey}`)}
      </div>
    </div>
  );
}

export default function About() {
  const { t } = useLanguage();
  return (
    <section
      data-testid="about-section"
      className="relative section-pad bg-[#0a0a0a]"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.9 }}
            className="lg:col-span-6 relative"
          >
            <div className="relative gold-frame p-3">
              <img
                src={ABOUT_IMG}
                alt="Technician repairing an appliance"
                className="w-full h-auto object-cover grayscale-[10%] contrast-110"
                loading="lazy"
              />
              <div className="absolute inset-3 ring-1 ring-[rgba(212,175,55,0.2)] pointer-events-none" />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.9, delay: 0.1 }}
            className="lg:col-span-6"
          >
            <span className="text-overline">{t("about.overline")}</span>
            <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl mt-3 leading-tight">
              <span className="text-white">{t("about.title").split(" ").slice(0, 2).join(" ")} </span>
              <span className="gold-gradient-text italic">
                {t("about.title").split(" ").slice(2).join(" ")}
              </span>
            </h2>
            <p className="mt-6 text-white/70 leading-relaxed max-w-xl">
              {t("about.desc")}
            </p>

            <div className="grid grid-cols-3 gap-4 sm:gap-6 mt-12">
              <Counter to={40} suffix="+" labelKey="counter_years" />
              <Counter to={1000} suffix="s" labelKey="counter_repairs" />
              <Counter to={5} suffix="★" labelKey="counter_rating" />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
